import { Dropdown, message } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { trackButtonClick, trackRoute } from '../../analytics/mixpanel';
import { DeleteDocIcon, EditDocIcon } from '../../assets';
import FolderExportOutlined from '../../assets/common/FolderExportOutlined';
import FolderOpenOutlined from '../../assets/common/FolderOpenOutlined';
import FolderPlusOutlined from '../../assets/common/FolderPlusOutlined';
import AngleRightOutlined from '../../assets/docchat/AngleRightOutlined';
import ClearBrushOutlined from '../../assets/docchat/ClearBrushOutlined';
import ThreeDotOutlined from '../../assets/docchat/ThreeDotOutlined';
import { useSelectedUploadData } from '../../Features/Chatbot/useSelectedUploadData';
import CreateFolderModal from '../../Features/FilePicker/CreateFolderModal';
import {
  deleteChat,
  deleteFile,
  getChatHistory,
  handleMoveToFolder,
  moveToFolder,
  removeFromFolder,
  renameFile,
} from '../../Redux/DocChat/docChatActions';
import { stopPropagationFunc } from '../../utils/functions';
import ConfirmationModal from '../UI/ConfirmationModal';
import UpdateFileNameModal from '../UI/UpdateFileNameModal';
import SettingsBreadCrumb from './SettingsBreadCrumb';

const initialModalData = {
  edit: false,
  delete: false,
  newFolder: false,
  data: null,
};

const HeaderDocChat = () => {
  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [prevLocation, updatePrevLocation] = useState('');
  const { userData } = useSelector((state) => state.loginState);
  const savedDocChatRecordStatus = useSelector(
    (state) => state.docChatData?.savedDocChatRecordStatus
  );
  const { record, position, records } = useSelectedUploadData();
  const [modalData, setModalData] = useState(initialModalData);
  const [loader, setLoader] = useState(null);

  const documentName = record?.document_name
    ? record.document_name
    : record?.documents
      ? record.documents?.[position]?.document_name
      : null;

  useEffect(() => {
    if (pathname && prevLocation !== pathname) {
      updatePrevLocation(pathname);
      trackRoute({ route: pathname });
    }
  }, [pathname]);

  const handleModal = (name, data) => {
    if (name === 'removeFromFolder') {
      removeFromFolder({
        collectionId: data?.collectionId,
        documentId: data?.document_id,
        navigate,
        pathname,
        dispatch,
      });
      return;
    }
    if (name === 'moveToFolder') {
      moveToFolder({
        collectionId: data?.collectionId,
        documentId: data?.collectionId
          ? record.documents?.[position]?.document_id
          : data?.document_id,
        userId: userData?.id,
        dispatch,
        pathname,
        navigate,
      });
      return;
    }
    setModalData((prev) => ({
      ...prev,
      [name]: true,
      ...(data ? { data } : {}),
    }));
  };

  const closeModal = () => {
    setModalData({ ...initialModalData });
  };

  const handleClearChat = (data) => {
    trackButtonClick({ type: 'Clear Chat from Header' });
    dispatch(
      deleteChat({
        collection_id: data?.collection_id,
        documentId: data?.document_id,
      })
    );
  };

  const editValidation = (newFileName) => {
    const nameExists = records?.some((doc) => {
      return doc.document_name === newFileName || doc.collection_name === newFileName;
    });
    if (nameExists) {
      message.error('Name already exists');
      if (loader) setLoader(null);
      return false;
    }
    return true;
  };

  const handleDeleteDoc = async () => {
    setLoader('DELETE');
    trackButtonClick({ type: 'Document Deleted from Header' });
    try {
      const documentId = record.documents?.[position]?.document_id;
      await deleteFile({ documentId });
      dispatch(getChatHistory({ userId: userData?.id }));
      if (pathname === '/viewer-docchat' && record?.document_id === documentId) {
        navigate('/docchat');
      }
    } finally {
      setLoader(null);
      closeModal();
    }
  };

  const handleEditDocName = async (newFileName) => {
    // trackButtonClick({ type: 'Document Renamed' });
    setLoader('EDIT');
    if (savedDocChatRecordStatus === 'LOADING') {
      message.error('Document list is loading, please try again after few seconds');
      return;
    }
    const nameExists = records?.some((doc) => {
      return doc.document_name === newFileName || doc.collection_name === newFileName;
    });
    if (nameExists) {
      message.error('Name already exists');
      return;
    }
    try {
      const collectionId = modalData.data?.collection_id;
      const documentId = collectionId
        ? modalData.data?.documents?.[position]?.document_id
        : modalData.data?.document_id;
      await renameFile({
        documentId,
        newName: newFileName,
        oldName: collectionId
          ? modalData.data?.documents?.[position]?.document_name
          : modalData.data?.document_name,
      });
      dispatch(getChatHistory({ userId: userData?.id }));
    } finally {
      setLoader(null);
      closeModal();
    }
  };

  const handleMoveToFolderFunc = (value) => {
    handleMoveToFolder({
      collectionName: value,
      documentId: modalData.data?.document_id,
      setLoader,
      closeModal,
      userId: userData?.id,
      dispatch,
      pathname,
      navigate,
    });
  };

  const moveItems = useMemo(() => {
    return [
      {
        key: '1',
        label: 'Move to a new folder',
        icon: (
          <div className='flex'>
            <FolderPlusOutlined />
          </div>
        ),
        onClick: (e) => {
          stopPropagationFunc(e);
          handleModal('newFolder', record);
        },
      },
      ...(records?.map((item) =>
        item?.collection_id && item?.collection_id !== record?.collection_id
          ? {
              key: item?.chat_id ?? item?.collection_id,
              label: item?.collection_name,
              icon: (
                <div className='flex'>
                  <FolderOpenOutlined size={14} />
                </div>
              ),
              onClick: (e) => {
                stopPropagationFunc(e);
                handleModal('moveToFolder', {
                  ...record,
                  collectionId: item?.collection_id,
                });
              },
            }
          : null
      ) ?? []),
    ];
  }, [records, record]);

  const items = [
    {
      key: 'edit',
      label: 'Rename file',
      icon: <EditDocIcon />,
      onClick: (e) => {
        stopPropagationFunc(e);
        handleModal('edit', record);
      },
    },
    {
      key: 'move',
      label: (
        <div className='flex align-center' style={{ justifyContent: 'space-between' }}>
          <div className='flex align-center' style={{ gap: '8px' }}>
            <FolderExportOutlined />
            <span>Move</span>
          </div>
          <AngleRightOutlined />
        </div>
      ),
      children: moveItems,
      popupClassName: 'move-sub-menu-popup',
    },
    record?.collection_id
      ? null
      : {
          key: 'clear',
          label: 'Clear Chat',
          icon: <ClearBrushOutlined />,
          onClick: (e) => {
            stopPropagationFunc(e);
            handleClearChat(record);
          },
        },
    {
      key: 'delete',
      label: 'Delete file',
      icon: <DeleteDocIcon />,
      onClick: (e) => {
        stopPropagationFunc(e);
        handleModal('delete', record);
      },
    },
  ];

  return (
    <>
      <div className='flex fs-14 align-center' style={{ gap: '8px' }}>
        {record?.collection_name && (
          <div className='flex' style={{ gap: '8px', paddingLeft: '1rem' }}>
            <FolderOpenOutlined size={14} />
            <span style={{ color: 'var(--grey-7)' }}>{record.collection_name}</span>
          </div>
        )}
        {documentName && documentName !== 'undefined' && (
          <>
            {record?.collection_name && <AngleRightOutlined size={18} fill='var(--grey-7)' />}
            <span style={{ color: '--grey-12', wordBreak: 'break-all' }}>{documentName}</span>
          </>
        )}
        {documentName && (
          <Dropdown
            menu={{ items }}
            trigger={['click']}
            overlayClassName='file-menu-dropdown'
            destroyOnHidden
          >
            <button
              type='button'
              className='action-btn'
              onClick={(e) => e.stopPropagation()}
              style={{ marginTop: '5px' }}
            >
              <ThreeDotOutlined />
            </button>
          </Dropdown>
        )}

        <SettingsBreadCrumb product='docchat' />
      </div>

      <UpdateFileNameModal
        isOpen={modalData.edit}
        initialText={
          modalData.data?.collection_id
            ? modalData.data?.documents?.[position]?.document_name
            : modalData.data?.document_name
        }
        onClose={closeModal}
        onSave={handleEditDocName}
        loading={loader === 'EDIT'}
        extraValidation={editValidation}
      />
      <ConfirmationModal
        isOpen={modalData.delete}
        text={`Are you sure you want to delete ${modalData.data?.collection_id ? 'folder' : 'file'} "${modalData.data?.[modalData.data?.collection_id ? 'collection_name' : 'document_name']}"?`}
        onCancel={closeModal}
        onSubmit={handleDeleteDoc}
        loading={loader === 'DELETE'}
        submitButtonProps={{ danger: true }}
      />
      <CreateFolderModal
        isOpen={modalData.newFolder}
        onClose={closeModal}
        onSave={handleMoveToFolderFunc}
        loading={loader === 'MOVE_TO_NEW_FOLDER'}
      />
    </>
  );
};

export default HeaderDocChat;
